<div class="cookie-policy-page">
    <header class="cookie-policy-page__topbar">
        <span class="cookie-policy-page__brand">{{ appName }}</span>
        <a class="cookie-policy-page__back" [href]="backRoute" translate>Back to sign in</a>
    </header>

    <div class="cookie-policy-page__layout">
        <nav class="cookie-policy-page__toc" aria-label="On this page">
            <span class="cookie-policy-page__toc-title" translate>On this page</span>
            <ol>
                <li *ngFor="let section of sections; let i = index">
                    <a [href]="pagePath + '#' + section.id"><span class="cookie-policy-page__toc-num">{{ i + 1 }}</span>{{ section.titleKey | translate }}</a>
                </li>
            </ol>
        </nav>

        <main class="cookie-policy-page__main">
            <h1 class="cookie-policy-page__title" translate>Cookie Policy</h1>
            <p class="cookie-policy-page__standfirst">
                {{ appName }} — what we store on your device, what you can choose, and how to change it.
            </p>

            <dl class="cookie-policy-page__meta">
                <div>
                    <dt translate>Version</dt>
                    <dd>{{ version }}</dd>
                </div>
                <div>
                    <dt translate>Effective date</dt>
                    <dd class="cookie-policy-page__pending" translate>To be confirmed</dd>
                </div>
                <div>
                    <dt translate>Last updated</dt>
                    <dd>{{ lastUpdated }}</dd>
                </div>
                <div>
                    <dt translate>Document owner</dt>
                    <dd>{{ owner }}</dd>
                </div>
            </dl>

            <p class="cookie-policy-page__lang-note" translate>This policy is published in English, and the English text governs.</p>

            <section id="scope">
                <h2><span class="cookie-policy-page__num">1</span>Purpose and scope</h2>
                <p>
                    This Cookie Policy explains how we use cookies and similar technologies when authorised users access
                    {{ appName }}, including the sign-in page and the authenticated environment. It should be read together
                    with the applicable Privacy Notice and the customer agreement governing use of this application.
                </p>
                <p>
                    It covers first-party technologies controlled through this application and third-party services that may
                    be activated in it. It does not describe cookies placed independently when you visit a third-party website
                    outside this application.
                </p>
            </section>

            <section id="what">
                <h2><span class="cookie-policy-page__num">2</span>What are cookies and similar technologies?</h2>
                <p>
                    Cookies are small data files stored by a browser on a device. They can support authentication, security,
                    session continuity, user preferences, analytics, application performance, product guidance and
                    customer-success activities. This application may also use browser storage, software development kits,
                    pixels or network requests that perform similar functions. In this policy, "cookies" includes these
                    comparable technologies where relevant.
                </p>
            </section>

            <section id="approach">
                <h2><span class="cookie-policy-page__num">3</span>Our approach to consent</h2>
                <ul>
                    <li>
                        This application uses strictly necessary cookies without an optional consent toggle, because they
                        support authentication, security, session management and recording your cookie preference.
                    </li>
                    <li>Optional services remain off unless you actively enable them.</li>
                    <li>
                        You can select "Reject optional cookies", "Manage preferences" or "Accept optional cookies". Rejecting
                        optional cookies does not reduce core functionality.
                    </li>
                    <li>No optional tracker, SDK, pixel, browser-storage identifier or vendor request runs before a valid choice is recorded.</li>
                    <li>If no valid saved choice exists, optional services remain off.</li>
                    <li>Consent is specific to the browser and application environment in which it is given.</li>
                </ul>
            </section>

            <section id="categories">
                <h2><span class="cookie-policy-page__num">4</span>Cookie categories</h2>
                <div class="cookie-policy-page__table-wrap">
                    <table class="cookie-policy-page__table">
                        <thead>
                            <tr>
                                <th translate>Category</th>
                                <th translate>Default</th>
                                <th translate>Treatment</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><span class="cookie-policy-page__pill cookie-policy-page__pill--necessary" translate>Strictly necessary</span></td>
                                <td translate>Always active</td>
                                <td>No optional toggle. Used only to provide or secure the application and to remember your choice.</td>
                            </tr>
                            <tr>
                                <td>Functional / preference</td>
                                <td translate>Off by default</td>
                                <td>Optional interface preferences. Activated only after consent. No service is in this category today.</td>
                            </tr>
                            <tr>
                                <td><span class="cookie-policy-page__pill cookie-policy-page__pill--analytics" translate>Analytics / performance</span></td>
                                <td translate>Off by default</td>
                                <td>Feature usage, application performance and reliability. Activated only after prior opt-in.</td>
                            </tr>
                            <tr *ngIf="hasCustomerSuccessCategory">
                                <td><span class="cookie-policy-page__pill cookie-policy-page__pill--success" translate>Customer success / product adoption</span></td>
                                <td translate>Off by default</td>
                                <td>
                                    In-product guidance, engagement and onboarding. Activated only after prior opt-in, because
                                    account, role, company, contact and usage data may be shared with external providers.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            <section id="inventory">
                <h2><span class="cookie-policy-page__num">5</span>Cookies and services we use</h2>
                <p>
                    The inventory below reflects the latest internal browser review. Exact values can change when providers,
                    configurations or product features change, and we will update this policy when material changes are
                    introduced.
                </p>
                <div class="cookie-policy-page__table-wrap">
                    <table class="cookie-policy-page__table">
                        <thead>
                            <tr>
                                <th translate>Technology</th>
                                <th translate>Provider</th>
                                <th translate>Category</th>
                                <th translate>Domain</th>
                                <th translate>Duration</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td class="cookie-policy-page__mono">oxm_cookie_consent</td>
                                <td translate>First-party</td>
                                <td><span class="cookie-policy-page__pill cookie-policy-page__pill--necessary" translate>Necessary</span></td>
                                <td>This domain</td>
                                <td class="cookie-policy-page__num-cell">12 months</td>
                            </tr>
                            <tr>
                                <td class="cookie-policy-page__mono">__oxm_cookie_probe</td>
                                <td translate>First-party</td>
                                <td><span class="cookie-policy-page__pill cookie-policy-page__pill--necessary" translate>Necessary</span></td>
                                <td>This domain</td>
                                <td class="cookie-policy-page__num-cell">Instant</td>
                            </tr>
                            <tr *ngIf="includeMixpanel">
                                <td class="cookie-policy-page__mono">mp_&lt;token&gt;_mixpanel</td>
                                <td>Mixpanel</td>
                                <td><span class="cookie-policy-page__pill cookie-policy-page__pill--analytics" translate>Analytics</span></td>
                                <td>This domain</td>
                                <td class="cookie-policy-page__num-cell">Up to 1 year</td>
                            </tr>
                            <tr>
                                <td class="cookie-policy-page__mono">site24x7rumID</td>
                                <td>Site24x7</td>
                                <td><span class="cookie-policy-page__pill cookie-policy-page__pill--analytics" translate>Analytics</span></td>
                                <td>This domain</td>
                                <td class="cookie-policy-page__num-cell">About 1 year</td>
                            </tr>
                            <tr *ngIf="includeChurnZero">
                                <td class="cookie-policy-page__mono">CzSession, CzWebUrl, CzUITheme</td>
                                <td>ChurnZero</td>
                                <td><span class="cookie-policy-page__pill cookie-policy-page__pill--success" translate>Customer success</span></td>
                                <td>Provider-controlled churnzero.net</td>
                                <td class="cookie-policy-page__num-cell">Session; one persists longer</td>
                            </tr>
                            <tr *ngIf="includeProductFruits">
                                <td class="cookie-policy-page__mono">Provider cookies and storage</td>
                                <td>ProductFruits</td>
                                <td><span class="cookie-policy-page__pill cookie-policy-page__pill--success" translate>Customer success</span></td>
                                <td>Provider-controlled domains</td>
                                <td class="cookie-policy-page__num-cell">Up to about 2 months</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            <section id="providers">
                <h2><span class="cookie-policy-page__num">6</span>More information about optional providers</h2>
                <ng-container *ngIf="includeMixpanel">
                    <h3>Mixpanel</h3>
                    <p>
                        If you enable Mixpanel, this application may send account and usage information to help us
                        understand how features are used and improve the product. Withdrawal stops future optional collection
                        once the preference takes effect, but it does not automatically erase information already received by
                        the provider.
                    </p>
                </ng-container>
                <h3>Site24x7</h3>
                <p>
                    Site24x7 is used for performance and reliability monitoring. Because the identifier it stores is durable,
                    it is classified as optional Analytics / performance and is off by default.
                </p>
                <ng-container *ngIf="includeProductFruits">
                    <h3>ProductFruits</h3>
                    <p>
                        ProductFruits provides in-product guidance and onboarding. It remains off until you opt in.
                    </p>
                </ng-container>
                <ng-container *ngIf="includeChurnZero">
                    <h3>ChurnZero</h3>
                    <p>
                        If enabled, ChurnZero supports customer-success and product-engagement activities. Some cookies are
                        placed on ChurnZero-controlled domains, which this application cannot delete directly. Withdrawing
                        consent stops the application from loading ChurnZero for future optional collection, while
                        provider-domain cookies may remain until they expire or you remove them through browser controls.
                    </p>
                </ng-container>
            </section>

            <section id="manage">
                <h2><span class="cookie-policy-page__num">7</span>How to manage or withdraw your choice</h2>
                <div class="cookie-policy-page__cta">
                    <p translate>You can review or change which optional services this application may use at any time.</p>
                    <button type="button" class="cookie-policy-page__btn" (click)="openPreferences()" translate>Manage preferences</button>
                </div>
                <ul>
                    <li>On first use, select "Reject optional cookies", "Manage preferences" or "Accept optional cookies".</li>
                    <li>After making a choice, open your account menu &gt; Cookie preferences to review or change it.</li>
                    <li>Changes take effect immediately. Optional services that are switched off stop loading and stop future collection.</li>
                    <li>
                        You can also delete or block cookies using browser settings. Doing so may remove the saved preference
                        and you may be asked to choose again.
                    </li>
                    <li>If you use a different browser, device or environment, a separate choice may be required.</li>
                    <li>
                        Withdrawing consent does not necessarily delete information already received by a provider, and it may
                        not remove cookies stored on a provider-controlled domain. You can use browser controls to delete such
                        cookies, and you may contact us regarding applicable privacy rights.
                    </li>
                </ul>
            </section>

            <section id="basis">
                <h2><span class="cookie-policy-page__num">8</span>Legal basis and strictly necessary technologies</h2>
                <p>
                    Where consent is required, we rely on your active consent before optional cookies or similar
                    technologies are used. Strictly necessary technologies are used because they are required to provide,
                    secure or maintain a service you requested, or to store your cookie choice. Where personal data is
                    processed through an optional service, the corresponding processing is described further in the
                    applicable Privacy Notice.
                </p>
            </section>

            <section id="international">
                <h2><span class="cookie-policy-page__num">9</span>International access and third parties</h2>
                <p>
                    Optional providers may process information in locations determined by their service configuration and
                    their contractual arrangements with us. The applicable Privacy Notice describes relevant
                    recipients, transfer safeguards and privacy rights. Provider websites and services are governed by their
                    own privacy information when you visit them directly.
                </p>
            </section>

            <section id="changes">
                <h2><span class="cookie-policy-page__num">10</span>Changes to this Cookie Policy</h2>
                <p>
                    We may update this Cookie Policy when features, providers, purposes, cookie durations, legal
                    requirements or technical configurations change. If a material change affects the purpose or the list of
                    optional services, the application invalidates the earlier preference and asks you to choose again. The
                    "Last updated" date above indicates the latest revision.
                </p>
            </section>
        </main>
    </div>
</div>
